import { css } from '@emotion/react'
import PreviousIcon from '@mui/icons-material/ChevronLeft'
import NextIcon from '@mui/icons-material/ChevronRight'
import { memo, useCallback, useEffect, useState } from 'react'
import { MergedPrivateGalleryUser } from '../../../../mfcLogic/api/privateGallery'
import { KeyEvent, registerKeyHandler } from '../../../../state/state.keys'
import IconButton from '../../../Buttons/IconButton'
import { palette } from '../../../styles/colors'

const rootStyles = css({
    flexDirection: 'column',
    borderTopRightRadius: 4,
    borderTopLeftRadius: 4,
    position: 'relative',
    overflow: 'hidden',
    overflowX: 'auto',
    display: 'flex',
    height: '100%',
    width: '100%',
})

const snapshotLabelStyles = css({
    fontWeight: 500,
    fontSize: 11,
    textAlign: 'center',
    marginTop: 6,
    marginBottom: 6,
    marginRight: 40,
})

const prevButtonStyles = css({
    position: 'absolute',
    background: palette.background,
    top: '57%',
    '&:hover': {
        background: palette.background,
    },
    boxShadow: '0px 0px 3px 1px ' + palette.dialogButtonShadows,
    left: -31,
})
const nextButtonStyles = css({
    position: 'absolute',
    background: palette.background,
    top: '57%',
    '&:hover': {
        background: palette.background,
    },
    boxShadow: '0px 0px 3px 1px ' + palette.dialogButtonShadows,
    right: -31,
})

export interface PrivateGalleryImageProps {
    currentPosition: number
    gallery: MergedPrivateGalleryUser
    galleries: Array<MergedPrivateGalleryUser>
}

function getNextImageInAlbum(idx, album) {
    if (idx === album.length - 1) return 0
    else return idx + 1
}

function getPreviousImageInAlbum(idx, album) {
    if (idx - 1 < 0) return album.length - 1
    else return idx - 1
}

const setDrawerTitle = (username) => {
    document.querySelector(
        '[data-testid="Fragment__Header"]',
    ).children[0].innerHTML = `${username}'s Gallery`
}

const PrivateGalleryImage = memo((props: PrivateGalleryImageProps) => {
    const { currentPosition, gallery, galleries } = props
    const [currentImage, setCurrentImage] = useState(currentPosition)
    const [currentGallery, setCurrentGallery] = useState(
        galleries.indexOf(gallery),
    )

    const openPrevious = useCallback(() => {
        const priorPosition = getPreviousImageInAlbum(
            currentImage,
            gallery.gallery,
        )
        setCurrentImage(priorPosition)
        if (priorPosition === 2) {
            setCurrentGallery(
                getPreviousImageInAlbum(currentGallery, galleries),
            )
        }
    }, [currentImage, currentGallery, gallery, galleries])

    const openNext = useCallback(() => {
        const nextPosition = getNextImageInAlbum(currentImage, gallery.gallery)
        setCurrentImage(nextPosition)
        if (nextPosition === 0) {
            setCurrentGallery(getNextImageInAlbum(currentGallery, galleries))
        }
    }, [currentImage, currentGallery, gallery, galleries])

    useEffect(() => {
        setDrawerTitle(galleries[currentGallery].username)
    }, [currentGallery])

    useEffect(() => {
        return registerKeyHandler(KeyEvent.Keydown, () => openPrevious(), [
            'ArrowLeft',
        ])
    }, [currentImage])

    useEffect(() => {
        return registerKeyHandler(KeyEvent.Keydown, () => openNext(), [
            'ArrowRight',
        ])
    }, [currentImage])

    const browsingButtons = (
        <>
            <IconButton
                data-testid="IconButton__Previous"
                css={prevButtonStyles}
                onClick={openPrevious}
            >
                <PreviousIcon />
            </IconButton>
            <IconButton
                data-testid="IconButton__Next"
                css={nextButtonStyles}
                onClick={openNext}
            >
                <NextIcon />
            </IconButton>
        </>
    )

    return (
        <>
            <div css={rootStyles}>
                <span css={snapshotLabelStyles} data-testid="Snapshot__Label">
                    Viewing Snapshot {currentImage + 1} of{' '}
                    {galleries[currentGallery].gallery.length}
                </span>
                <img
                    src={galleries[currentGallery].gallery[currentImage]}
                    data-testid="Image__GalleryModal"
                />
            </div>
            {browsingButtons}
        </>
    )
})

export default PrivateGalleryImage
